// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import { groupItems } from '@/features/game-definitions/groups';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';
import { formatToPar } from './components/Charts';
import { EmptyHint, StatBarRow, StatCard, StatTile, StatTileRow } from './components/StatPieces';
import { formatMerits, formatPosition, formatShortDate } from './formatting';
import type { PlayerStatistics, StatisticsGameDefinition } from './types';

/**
 * The landing view. Goffy is about playing games with friends, so this opens on the competitive
 * story — where the player stands in their tours, what they have won, what they played last —
 * and leaves the scoring averages to the second view.
 */
export function GamesToursView({
  statistics,
  gameDefinitions,
}: {
  statistics: PlayerStatistics;
  gameDefinitions: StatisticsGameDefinition[];
}) {
  const { t } = useI18n();
  const router = useRouter();
  const { overview, activeTours, trophies, games, recentRounds } = statistics;

  const definitionsById = useMemo(() => {
    const map = new Map<string, StatisticsGameDefinition>();
    gameDefinitions.forEach((definition) => {
      if (definition?.id) {
        map.set(String(definition.id).toLowerCase(), definition);
      }
    });
    return map;
  }, [gameDefinitions]);

  const roundsDelta = useMemo(() => {
    const previous = overview.previousYearRoundsPlayed;
    if (previous === null || previous === 0) {
      return null;
    }
    const diff = overview.roundsPlayed - previous;
    if (diff === 0) {
      return t('statistics.sameAsLastSeason');
    }
    return t('statistics.vsLastSeason', { delta: diff > 0 ? `+${diff}` : String(diff) });
  }, [overview.previousYearRoundsPlayed, overview.roundsPlayed, t]);

  const maxGameRounds = Math.max(1, ...games.map((game) => game.roundsPlayed));

  // Hazards, achievements and challenges sit alongside the main games in the payload, which reads
  // as one loose list — a bunker tally next to Stableford. Bucket them under the same headers the
  // leaderboard and round-results screens already use, so the taxonomy is the app's, not this
  // screen's. Bars stay scaled against the overall maximum so lengths compare across groups.
  const gameGroups = useMemo(
    () =>
      groupItems(
        games,
        (game) => game.gameId,
        (game) => getGameDisplayName(definitionsById.get(game.gameId.toLowerCase()))
      ),
    [definitionsById, games]
  );

  return (
    <View style={styles.container}>
      <StatTileRow>
        <StatTile
          testID="stat-rounds-played"
          value={String(overview.roundsPlayed)}
          label={t('statistics.roundsPlayed')}
          delta={roundsDelta}
          deltaTone={
            overview.previousYearRoundsPlayed !== null
            && overview.roundsPlayed < overview.previousYearRoundsPlayed
              ? 'neutral'
              : 'good'
          }
        />
        <StatTile
          value={String(overview.tours)}
          suffix={overview.activeTours > 0 ? t('statistics.activeSuffix', { count: overview.activeTours }) : undefined}
          label={t('statistics.tours')}
        />
        <StatTile value={String(overview.gamesPlayed)} label={t('statistics.gamesPlayed')} />
        <StatTile
          testID="stat-merits"
          value={formatMerits(overview.meritPoints)}
          label={t('statistics.meritsEarned')}
          gold
        />
      </StatTileRow>

      <StatCard title={t('statistics.activeTours')}>
        {activeTours.length === 0 ? (
          <EmptyHint text={t('statistics.noActiveTours')} />
        ) : (
          activeTours.map((tour) => (
            <Pressable
              key={tour.tourId}
              style={styles.tourRow}
              testID={`stat-tour-${tour.tourId}`}
              accessibilityRole="button"
              onPress={() => router.push(`/tour/${tour.tourId}/leaderboard`)}
            >
              <View style={[styles.position, tour.position === 1 && styles.positionGold]}>
                <Text style={[styles.positionText, tour.position === 1 && styles.positionTextGold]}>
                  {formatPosition(tour.position)}
                </Text>
              </View>
              <View style={styles.tourMeta}>
                <Text style={styles.tourName} numberOfLines={1}>
                  {tour.name}
                </Text>
                <Text style={styles.tourSub} numberOfLines={1}>
                  {tour.position === 1
                    ? t('statistics.tourLeading', { players: tour.playerCount })
                    : t('statistics.tourBehind', {
                        players: tour.playerCount,
                        merits: formatMerits(tour.pointsBehindLeader),
                      })}
                </Text>
              </View>
              <Text style={styles.chevron}>›</Text>
            </Pressable>
          ))
        )}
      </StatCard>

      <StatCard title={t('statistics.trophyCabinet')}>
        <View style={styles.trophies}>
          <Trophy value={trophies.tourWins} label={t('statistics.tourWins')} />
          <Trophy value={trophies.tourRunnerUps} label={t('statistics.tourRunnerUps')} />
          <Trophy value={trophies.tourPodiums} label={t('statistics.tourPodiums')} />
          <Trophy value={trophies.roundWins} label={t('statistics.roundWins')} />
        </View>
        <Text style={styles.cabinetNote}>{t('statistics.trophyCabinetNote')}</Text>
      </StatCard>

      <StatCard title={t('statistics.gamesPlayedAndWon')}>
        {games.length === 0 ? (
          <EmptyHint text={t('statistics.noGames')} />
        ) : (
          gameGroups.map((group) => (
            <View key={group.groupId} style={styles.gameGroup} testID={`stat-game-group-${group.groupId}`}>
              <Text style={styles.gameGroupTitle}>{group.title}</Text>
              {group.items.map((game) => {
                const definition = definitionsById.get(game.gameId.toLowerCase());
                return (
                  <StatBarRow
                    key={game.gameId}
                    testID={`stat-game-${game.gameId}`}
                    label={getGameDisplayName(definition, t('statistics.unknownGame'))}
                    fraction={game.roundsPlayed / maxGameRounds}
                    color={game.wins > 0 ? appColors.accent.primary : appColors.background.interactiveMuted}
                    value={t('statistics.gameWinsOfRounds', { wins: game.wins, rounds: game.roundsPlayed })}
                    layout="stacked"
                  />
                );
              })}
            </View>
          ))
        )}
      </StatCard>

      <StatCard title={t('statistics.recentRounds')}>
        {recentRounds.length === 0 ? (
          <EmptyHint text={t('statistics.noRounds')} />
        ) : (
          recentRounds.map((round) => (
            <Pressable
              key={round.roundId}
              style={styles.roundRow}
              testID={`stat-round-${round.roundId}`}
              accessibilityRole="button"
              onPress={() => router.push(`/round/${round.roundId}/scorecard`)}
            >
              <Text style={styles.roundDate}>{formatShortDate(round.playDate, round.playedAtUtc)}</Text>
              <Text style={styles.roundCourse} numberOfLines={1}>
                {round.courseName}
              </Text>
              <Text style={styles.roundScore} numberOfLines={1}>
                {t('statistics.roundScore', {
                  gross: round.grossStrokes,
                  toPar: formatToPar(round.toPar),
                })}
              </Text>
              {round.position !== null && round.fieldSize > 1 ? (
                <Text style={[styles.roundPosition, round.position === 1 && styles.roundPositionGold]}>
                  {t('statistics.positionOf', {
                    position: formatPosition(round.position),
                    total: round.fieldSize,
                  })}
                </Text>
              ) : (
                <Text style={styles.roundPositionMuted}>{formatMerits(round.meritPoints)}</Text>
              )}
            </Pressable>
          ))
        )}
      </StatCard>
    </View>
  );
}

function Trophy({ value, label }: { value: number; label: string }) {
  return (
    <View style={styles.trophy}>
      <Text style={[styles.trophyValue, value === 0 && styles.trophyValueEmpty]}>{value}</Text>
      <Text style={styles.trophyLabel} numberOfLines={2}>
        {label}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 10,
  },
  tourRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 6,
  },
  position: {
    minWidth: 44,
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 4,
    borderRadius: 9,
    backgroundColor: appColors.background.elevated,
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  positionGold: {
    borderColor: appColors.accent.goldBorder,
    backgroundColor: appColors.accent.goldSoft,
  },
  positionText: {
    fontSize: 13,
    fontWeight: '800',
    color: appColors.accent.primary,
  },
  positionTextGold: {
    color: appColors.accent.gold,
  },
  tourMeta: {
    flex: 1,
    minWidth: 0,
  },
  tourName: {
    fontSize: 13,
    fontWeight: '700',
    color: appColors.text.heading,
  },
  tourSub: {
    fontSize: 11.5,
    color: appColors.text.muted,
  },
  chevron: {
    fontSize: 16,
    color: appColors.text.muted,
  },
  trophies: {
    flexDirection: 'row',
    gap: 8,
  },
  trophy: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: appColors.background.panelAlt,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    borderRadius: 10,
    paddingHorizontal: 4,
    paddingVertical: 8,
  },
  trophyValue: {
    fontSize: 18,
    fontWeight: '800',
    color: appColors.accent.gold,
  },
  trophyValueEmpty: {
    color: appColors.text.muted,
  },
  trophyLabel: {
    fontSize: 10.5,
    textAlign: 'center',
    color: appColors.text.muted,
    marginTop: 1,
  },
  cabinetNote: {
    fontSize: 10.5,
    color: appColors.text.muted,
  },
  gameGroup: {
    marginTop: 6,
  },
  gameGroupTitle: {
    fontSize: 10.5,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: appColors.text.muted,
    marginBottom: 2,
  },
  roundRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
    paddingVertical: 5,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: appColors.border.strong,
  },
  roundDate: {
    width: 46,
    fontSize: 11,
    color: appColors.text.muted,
  },
  roundCourse: {
    flex: 1,
    minWidth: 0,
    fontSize: 12.5,
    fontWeight: '600',
    color: appColors.text.heading,
  },
  roundScore: {
    fontSize: 12,
    color: appColors.text.secondary,
  },
  roundPosition: {
    fontSize: 12,
    fontWeight: '800',
    color: appColors.accent.primary,
  },
  roundPositionGold: {
    color: appColors.accent.gold,
  },
  roundPositionMuted: {
    fontSize: 12,
    color: appColors.text.muted,
  },
});
